import { useState, useEffect, useCallback, useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { FileJson, Trash2, RefreshCw, Download, Search } from "lucide-react";
import { toast } from "sonner";
import RecordBrowser from "./RecordBrowser";
import UploadQueue from "./UploadQueue";

const API = process.env.REACT_APP_BACKEND_URL;
const authHdr = (token) => ({ Authorization: `Bearer ${token}` });
const GRADE_OPTIONS = Array.from({ length: 8 }, (_, i) => `Grade ${i + 1}`);

function LiveStatusChip({ status }) {
  const live = status === "live";
  return (
    <Badge
      variant="outline"
      className={`text-[10px] ${live ? "text-emerald-700 border-emerald-300 bg-emerald-50" : "text-slate-500 border-slate-300 bg-slate-50"}`}
      data-testid={`registry-live-status-${status}`}
    >
      {live ? "Live" : "Stale"}
    </Badge>
  );
}

export default function DatasetsTab({ token }) {
  const [registry, setRegistry] = useState([]);
  const [registryLoading, setRegistryLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [browsingFile, setBrowsingFile] = useState(null);

  const [search, setSearch] = useState("");
  const [gradeFilter, setGradeFilter] = useState("");
  const [subjectFilter, setSubjectFilter] = useState("");
  const [boardFilter, setBoardFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const loadRegistry = useCallback(async () => {
    setRegistryLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/cr/dataset-ops/registry`, { headers: authHdr(token) });
      const json = await res.json();
      setRegistry(json.files || []);
    } catch { toast.error("Failed to load dataset registry"); }
    finally { setRegistryLoading(false); }
  }, [token]);

  useEffect(() => { loadRegistry(); }, [loadRegistry]);

  const subjectOptions = useMemo(
    () => Array.from(new Set(registry.flatMap((f) => f.subjects || []))).sort(),
    [registry]
  );
  const boardOptions = useMemo(
    () => Array.from(new Set(registry.map((f) => f.board).filter(Boolean))).sort(),
    [registry]
  );

  const filteredRegistry = useMemo(() => {
    return registry.filter((f) => {
      if (search && !f.filename.toLowerCase().includes(search.toLowerCase())) return false;
      if (gradeFilter) {
        if (gradeFilter === "__unknown__" ? (f.grades || []).length : !(f.grades || []).includes(gradeFilter)) return false;
      }
      if (subjectFilter) {
        if (subjectFilter === "__unknown__" ? (f.subjects || []).length : !(f.subjects || []).includes(subjectFilter)) return false;
      }
      if (boardFilter) {
        if (boardFilter === "__unknown__" ? f.board : f.board !== boardFilter) return false;
      }
      if (statusFilter && f.live_status !== statusFilter) return false;
      return true;
    });
  }, [registry, search, gradeFilter, subjectFilter, boardFilter, statusFilter]);

  const handleDeleteFile = async () => {
    try {
      const res = await fetch(`${API}/api/admin/cr/dataset-ops/registry/${encodeURIComponent(deleteTarget)}`, {
        method: "DELETE", headers: authHdr(token),
      });
      if (!res.ok) { toast.error("Delete failed"); return; }
      toast.success(`Removed ${deleteTarget}`);
      loadRegistry();
    } catch { toast.error("Network error during delete"); }
    finally { setDeleteTarget(null); }
  };

  const handleDownloadFile = (filename) => {
    fetch(`${API}/api/admin/cr/dataset-ops/registry/${encodeURIComponent(filename)}/download`, { headers: authHdr(token) })
      .then(async (res) => {
        if (!res.ok) { toast.error("Download failed"); return; }
        const blob = await res.blob();
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.click();
        URL.revokeObjectURL(link.href);
      })
      .catch(() => toast.error("Network error during download"));
  };

  if (browsingFile) {
    return <RecordBrowser filename={browsingFile} token={token} onBack={() => setBrowsingFile(null)} />;
  }

  return (
    <div className="space-y-6">
      <UploadQueue token={token} onCommitted={loadRegistry} />

      {/* Dataset file registry */}
      <Card className="p-5 border border-slate-200 shadow-none">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-800">Dataset File Registry (/app/backend/)</h3>
          <Button variant="outline" size="sm" onClick={loadRegistry} disabled={registryLoading} data-testid="refresh-registry-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${registryLoading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input
              value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search filename…"
              className="pl-8 h-8 w-48 text-sm" data-testid="registry-search-input"
            />
          </div>
          <select
            className="border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm bg-white"
            value={gradeFilter} onChange={(e) => setGradeFilter(e.target.value)} data-testid="registry-grade-filter"
          >
            <option value="">All Grades</option>
            {GRADE_OPTIONS.map((g) => <option key={g} value={g}>{g}</option>)}
            <option value="__unknown__">Unknown</option>
          </select>
          <select
            className="border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm bg-white"
            value={subjectFilter} onChange={(e) => setSubjectFilter(e.target.value)} data-testid="registry-subject-filter"
          >
            <option value="">All Subjects</option>
            {subjectOptions.map((s) => <option key={s} value={s}>{s}</option>)}
            <option value="__unknown__">Unknown</option>
          </select>
          <select
            className="border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm bg-white"
            value={boardFilter} onChange={(e) => setBoardFilter(e.target.value)} data-testid="registry-board-filter"
          >
            <option value="">All Boards</option>
            {boardOptions.map((b) => <option key={b} value={b}>{b}</option>)}
            <option value="__unknown__">Unknown</option>
          </select>
          <select
            className="border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm bg-white"
            value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="registry-status-filter"
          >
            <option value="">All Statuses</option>
            <option value="live">Live</option>
            <option value="stale">Stale</option>
          </select>
        </div>

        {registryLoading ? (
          <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 animate-spin text-slate-300" /></div>
        ) : (
          <div className="overflow-x-auto" data-testid="dataset-registry-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs text-slate-500 uppercase tracking-wide">
                  <th className="py-2 pr-4">Filename</th>
                  <th className="py-2 pr-4">Board</th>
                  <th className="py-2 pr-4">Grade</th>
                  <th className="py-2 pr-4">Subject</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Last Modified</th>
                  <th className="py-2 pr-4 text-right">Records</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {filteredRegistry.map((f) => (
                  <tr key={f.filename} className="border-b border-slate-100" data-testid={`registry-row-${f.filename}`}>
                    <td className="py-2 pr-4">
                      <button
                        className="flex items-center gap-1.5 text-sky-700 hover:text-sky-900 hover:underline"
                        onClick={() => setBrowsingFile(f.filename)}
                        data-testid={`registry-filename-${f.filename}`}
                      >
                        <FileJson className="w-3.5 h-3.5 text-slate-400" />{f.filename}
                      </button>
                    </td>
                    <td className="py-2 pr-4 text-slate-600 text-xs">{f.board || "Unknown"}</td>
                    <td className="py-2 pr-4 text-slate-600 text-xs">{(f.grades || []).join(", ") || "Unknown"}</td>
                    <td className="py-2 pr-4 text-slate-600 text-xs">{(f.subjects || []).join(", ") || "Unknown"}</td>
                    <td className="py-2 pr-4"><LiveStatusChip status={f.live_status} /></td>
                    <td className="py-2 pr-4 text-slate-500 text-xs">{new Date(f.last_modified).toLocaleString()}</td>
                    <td className="py-2 pr-4 text-right text-slate-600">{f.record_count ?? "—"}</td>
                    <td className="py-2 text-right flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleDownloadFile(f.filename)} data-testid={`download-dataset-file-${f.filename}`}>
                        <Download className="w-3.5 h-3.5 text-slate-500" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(f.filename)} data-testid={`delete-dataset-file-${f.filename}`}>
                        <Trash2 className="w-3.5 h-3.5 text-red-500" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredRegistry.length === 0 && (
              <p className="text-sm text-slate-400 text-center py-8">No files match these filters.</p>
            )}
          </div>
        )}
      </Card>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove dataset file?</AlertDialogTitle>
            <AlertDialogDescription>
              This deletes <strong>{deleteTarget}</strong> from the server disk. Records already committed to fuel_questions are unaffected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-dataset-file">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteFile} data-testid="confirm-delete-dataset-file">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
